import { OAuth2QueryParameters } from "@authien/features/authentication/constants/Oauth2";

interface StateEntry {
  state: string;
  timestamp: number;
  used: boolean;
}

const STATE_EXPIRATION_TIME = 15 * 60 * 1000; // 15 minutes in milliseconds (for pending states)
const USED_STATE_MAX_AGE = 24 * 60 * 60 * 1000; // 24 hours in milliseconds (for used states)
const CLEANUP_INTERVAL = 5 * 60 * 1000; // Clean up every 5 minutes
const MAX_STATES = 500; // Maximum number of states to keep in localStorage

/**
 * Stores a state with a timestamp when OAuth flow is initiated.
 * States are marked as "pending" (not used) until the flow completes.
 * 
 * @param state - The OAuth2 state parameter
 */
export function setState(state: string): void {
  if (!state) return;
  
  const states = getStoredStates();
  const existingIndex = states.findIndex(entry => entry.state === state);
  
  if (existingIndex >= 0) {
    // Update existing entry - reset timestamp if not used
    if (!states[existingIndex].used) {
      states[existingIndex].timestamp = Date.now();
    }
  } else {
    // Add new entry as pending (not used)
    states.push({
      state,
      timestamp: Date.now(),
      used: false
    });
  }
  
  saveStates(states);
  cleanupExpiredStates();
}

/**
 * Marks a state as used when OAuth flow completes successfully.
 * Used states are kept to prevent replay attacks.
 * 
 * @param state - The OAuth2 state parameter
 */
export function markStateAsUsed(state: string): void {
  if (!state) return;
  
  const states = getStoredStates();
  const entry = states.find(e => e.state === state);
  
  if (entry) {
    entry.used = true;
    entry.timestamp = Date.now(); // Update timestamp when marked as used
    saveStates(states);
  }
}

/**
 * Deletes a state from storage (used when OAuth flow fails or is cancelled).
 * 
 * @param state - The OAuth2 state parameter
 */
export function deleteState(state: string): void {
  if (!state) return;
  
  const states = getStoredStates();
  const filtered = states.filter(entry => entry.state !== state);
  saveStates(filtered);
}

/**
 * Verifies if a state is valid (not used and not expired).
 * Returns true if state is valid (can be used), false if invalid (already used or expired).
 * 
 * @param state - The OAuth2 state parameter
 * @returns true if state is valid, false if invalid
 */
function isStateExpired(entry: StateEntry): boolean {
  const age = Date.now() - entry.timestamp;
  return age > STATE_EXPIRATION_TIME;
}

export function verifyState(state: string): boolean {
  if (!state) return false;
  
  cleanupExpiredStates();
  const states = getStoredStates();
  const entry = states.find(e => e.state === state);
  
  if (!entry) return true;
  if (entry.used) return false;

  if (isStateExpired(entry)) {
    deleteState(state);
    return false;
  }

  return true;
}

/**
 * Gets all stored states from localStorage.
 */
function getStoredStates(): StateEntry[] {
  try {
    const stored = localStorage.getItem(OAuth2QueryParameters.STATE);
    if (!stored) return [];
    
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error('Error reading states from localStorage:', error);
    return [];
  }
}

/**
 * Saves states to localStorage.
 * Handles quota exceeded errors by cleaning up old states and retrying.
 */
function isQuotaExceeded(error: any): boolean {
  return error?.name === 'QuotaExceededError' || error?.code === 22;
}

function trySaveReduced(states: StateEntry[]): void {
  const sortedStates = [...states].sort((a, b) => a.timestamp - b.timestamp);
  const reducedStates = sortedStates.slice(-Math.floor(states.length / 2));
  try {
    localStorage.setItem(OAuth2QueryParameters.STATE, JSON.stringify(reducedStates));
    console.log(`Reduced states from ${states.length} to ${reducedStates.length}`);
  } catch (retryError) {
    console.error('Error saving states after cleanup:', retryError);
    try { localStorage.removeItem(OAuth2QueryParameters.STATE); } catch (e) { /* ignore */ }
  }
}

function saveStates(states: StateEntry[]): void {
  try {
    localStorage.setItem(OAuth2QueryParameters.STATE, JSON.stringify(states));
  } catch (error: any) {
    if (isQuotaExceeded(error)) {
      console.warn('localStorage quota exceeded, cleaning up old states...');
      trySaveReduced(states);
    } else {
      console.error('Error saving states to localStorage:', error);
    }
  }
}

/**
 * Removes expired states from storage.
 * - Pending states expire after STATE_EXPIRATION_TIME (15 minutes)
 * - Used states expire after USED_STATE_MAX_AGE (24 hours)
 * - Limits total number of states to MAX_STATES (500)
 */
function getMaxAge(entry: StateEntry): number {
  return entry.used ? USED_STATE_MAX_AGE : STATE_EXPIRATION_TIME;
}

export function cleanupExpiredStates(): void {
  const states = getStoredStates();
  const now = Date.now();
  
  const validStates = states.filter(entry => {
    const age = now - entry.timestamp;
    return age <= getMaxAge(entry);
  });
  
  // Sort by timestamp (newest first) and limit to MAX_STATES
  const sortedStates = validStates.sort((a, b) => b.timestamp - a.timestamp);
  const limitedStates = sortedStates.slice(0, MAX_STATES);
  
  // Only save if we removed states or limited the count
  if (limitedStates.length !== states.length) {
    saveStates(limitedStates);
  }
}

// Cleanup expired states periodically
if (typeof window !== 'undefined') {
  setInterval(cleanupExpiredStates, CLEANUP_INTERVAL);
}
